<template>
  <!-- Test webhook -->
  <AlertDialog :open="showTestWebhookForm" @update:open="(o) => !o && (showTestWebhookForm = false)">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Test webhook</AlertDialogTitle>
        <AlertDialogDescription>Select an event to test</AlertDialogDescription>
      </AlertDialogHeader>
      <div class="space-y-2">
        <label class="text-sm font-medium" for="test-event-request-event-type">Select event</label>
        <Select v-model="testEventRequest.eventType">
          <SelectTrigger id="test-event-request-event-type" class="w-full">
            <SelectValue placeholder="Select event" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="o in testableScopes" :key="o.id" :value="o.id">{{ o.text }}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <AlertDialogFooter class="gap-2">
        <AlertDialogCancel :disabled="actionLoading" @click="showTestWebhookForm = false">Cancel</AlertDialogCancel>
        <AlertDialogAction :disabled="testEventRequest.eventType == '' || actionLoading" @click="createTestWebhook()">
          <Loader2 v-if="actionLoading" class="w-4 h-4 animate-spin" />
          {{ actionLoading ? 'Sending…' : 'Test event' }}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <!-- Edit scopes: a right drawer rather than a centre dialog — there are 20 scopes across
       several groups, which overflowed a fixed-height modal. -->
  <Drawer direction="right" :open="showEditScopeModal" @update:open="(o) => !o && (showEditScopeModal = false)">
    <DrawerContent class="sm:max-w-lg">
      <DrawerHeader class="text-left">
        <DrawerTitle>Edit scopes</DrawerTitle>
        <DrawerDescription>Select the events this webhook should receive</DrawerDescription>
      </DrawerHeader>
      <div class="flex flex-col gap-3 overflow-y-auto px-4 flex-1">
        <WebhookScopeSelect v-model="editScopeRequest.scope" :options="scope" />
      </div>
      <DrawerFooter class="flex-row justify-end gap-2">
        <Button variant="outline" :disabled="actionLoading" @click="showEditScopeModal = false">Cancel</Button>
        <Button :disabled="editScopeRequest.scope.length == 0 || actionLoading" @click="updateScope()">
          <Loader2 v-if="actionLoading" class="w-4 h-4 animate-spin" />
          {{ actionLoading ? 'Saving…' : 'Save scopes' }}
        </Button>
      </DrawerFooter>
    </DrawerContent>
  </Drawer>

  <div class="w-full h-full space-y-4">
    <!-- No "Back to webhooks" button: the top-bar breadcrumb (see setBreadcrumb) is the way back. -->
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0 space-y-1">
        <div class="flex items-center gap-2">
          <h2 class="text-lg font-semibold tracking-tight">Webhook</h2>
          <Badge v-if="webhook?.webhook?.active" variant="success">Active</Badge>
          <Badge v-else-if="webhook" variant="secondary">Paused</Badge>
        </div>
        <p class="text-sm text-muted-foreground truncate">{{ webhook?.webhook?.url }}</p>
      </div>
    </div>

    <div v-if="webhook != undefined" class="space-y-4">
        <!-- Actions -->
        <div class="flex flex-wrap items-center gap-2">
          <Button id="editScope" size="sm" variant="outline" :disabled="actionLoading" @click="openEditScope()">
            <Settings class="w-4 h-4" />Edit scopes
          </Button>
          <Button v-if="showTestWebhookButton" size="sm" variant="outline" :disabled="actionLoading" @click="showTestWebhookForm = true">
            <Send class="w-4 h-4" />Test webhook
          </Button>
          <Button v-if="webhook.webhook.active" size="sm" :disabled="actionLoading" @click="deactivateWebhook()">
            <CirclePause class="w-4 h-4" />Pause
          </Button>
          <Button v-if="!webhook.webhook.active" size="sm" :disabled="actionLoading" @click="activateWebhook()">
            <CirclePlay class="w-4 h-4" />Activate
          </Button>
          <Button size="sm" variant="destructive" :disabled="actionLoading" @click="deleteWebhook()">
            <Trash2 class="w-4 h-4" />Delete
          </Button>
        </div>

        <!-- Summary -->
        <!-- Status is already shown as a badge beside the page title; a second copy here
             was the same information twice. -->
        <div class="rounded-lg border border-border p-3 space-y-3">
          <div class="flex items-center gap-2">
            <p class="text-xs font-semibold min-w-16">Secret</p>
            <Badge v-if="showSecret" variant="outline" class="font-mono">{{ webhook.webhook.secret }}</Badge>
            <Button v-if="!showSecret" size="sm" variant="outline" @click="showSecret = true">
              <Eye class="w-4 h-4" />Show
            </Button>
            <Button v-else size="sm" variant="outline" @click="showSecret = false">
              <EyeOff class="w-4 h-4" />Hide
            </Button>
          </div>
          <div class="flex items-start gap-2">
            <p class="text-xs font-semibold min-w-16 pt-1">Scope</p>
            <div class="flex flex-wrap gap-1">
              <Badge v-for="item in webhook.webhook.scope" :key="item" variant="outline" class="text-xs">{{ item }}</Badge>
            </div>
          </div>
        </div>

        <!-- Event list (left) and the selected event (right), side by side rather than stacked. -->
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
        <div class="min-w-0">
          <p class="text-sm font-medium mb-2">Event list</p>
          <DataTable
            :headers="headersTable1"
            :items="webhook.webhookEvents.content"
            :loading="loading"
            :error="error"
            @retry="getWebhook(serverOptions.page - 1, serverOptions.rowsPerPage)"
            v-model:server-options="serverOptions"
            :server-items-length="serverItemsLength"
            @row-click="showData"
          >
            <template #item-eventType="eventType">
              <Badge variant="outline">{{ eventType.eventType }}</Badge>
            </template>
            <template #item-createdOn="createdOn">
              <span class="text-xs text-muted-foreground tabular-nums">{{ niceDateTime(createdOn.createdOn) }}</span>
            </template>
            <template #item-delivered="delivered">
              <div class="flex justify-end">
                <Badge v-if="delivered.delivered" variant="success">Success</Badge>
                <Badge v-else variant="destructive">Failed</Badge>
              </div>
            </template>
            <template #item-view="event">
              <div class="flex justify-end items-center gap-1">
                <Button variant="outline" size="sm" @click="showData(event)">View</Button>
              </div>
            </template>
          </DataTable>
        </div>

        <!-- Selected event -->
        <div v-if="selectedEvent != undefined" class="min-w-0 rounded-lg border border-border p-3 lg:sticky lg:top-4">
          <div class="flex items-start justify-between gap-3 mb-2">
            <div class="min-w-0">
              <p class="text-sm font-medium">Event</p>
              <p class="text-xs text-muted-foreground">Created on: {{ niceDateTime(selectedEvent.createdOn) }}</p>
            </div>
            <Button v-if="!selectedEvent.delivered" size="sm" :disabled="actionLoading" @click="resend(selectedEvent)">
              <Loader2 v-if="actionLoading" class="w-4 h-4 animate-spin" />
              {{ actionLoading ? 'Resending…' : 'Resend event' }}
            </Button>
          </div>
          <p class="text-xs mb-2 break-all">Id: {{ selectedEvent.id }}</p>
          <div class="flex items-center gap-2 mb-2">
            <Badge variant="outline">{{ selectedEvent.eventType }}</Badge>
            <Badge v-if="selectedEvent.delivered" variant="default">Success</Badge>
            <Badge v-else variant="destructive">Failed to deliver</Badge>
          </div>
          <div class="py-2 px-3 bg-muted rounded-lg text-xs overflow-x-auto">
            <vue-json-pretty :data="selectedEvent.content" style="font-size: 11px" />
          </div>
        </div>
        </div>
    </div>
  </div>
</template>

<script>
import {
  Button, Badge,
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter,
  toast,
} from '@boogi/ui'
import { Settings, Send, CirclePause, CirclePlay, Trash2, Eye, EyeOff, Loader2 } from 'lucide-vue-next'
import VueJsonPretty from 'vue-json-pretty'
import 'vue-json-pretty/lib/styles.css'
import { DateTime } from 'luxon'
import { breadcrumbState } from '@/stores/breadcrumb'
import { DataTable } from '@boogi/ui'
import WebhookScopeSelect from '@/components/WebhookScopeSelect.vue'
import { WEBHOOK_SCOPES } from '@/constants/webhookScopes'

export default {
  name: 'WebhookDetail',
  components: {
    DataTable,
    WebhookScopeSelect,
    Button,
    Badge,
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogAction,
    AlertDialogCancel,
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerDescription,
    DrawerFooter,
    VueJsonPretty,
    Settings,
    Send,
    CirclePause,
    CirclePlay,
    Trash2,
    Eye,
    EyeOff,
    Loader2,
  },

  data() {
    return {
      showTestWebhookForm: false,
      showTestWebhookButton:
        localStorage.getItem('apiUrl')?.includes('sandbox') ||
        localStorage.getItem('apiUrl')?.includes('dev'),
      showEditScopeModal: false,
      editScopeRequest: { scope: [] },
      scope: WEBHOOK_SCOPES,

      testEventRequest: {
        eventType: '',
      },

      webhook: undefined,
      selectedEvent: undefined,
      showSecret: false,
      serverOptions: {
        page: 1,
        rowsPerPage: 25,
      },
      serverItemsLength: undefined,
      loading: true,
      // The test/resend actions drive their own spinner. Sharing `loading` with the event
      // table meant a failed test request left the table spinning forever.
      actionLoading: false,
      error: false,
      headersTable1: [
        { text: 'Event', value: 'eventType' },
        { text: 'Created on', value: 'createdOn' },
        { text: 'Delivery status', value: 'delivered', align: 'right' },
        { text: 'Action', value: 'view' },
      ],
    }
  },
  computed: {
    webhookId() {
      return this.$route.params.webhookId
    },
    // Only the events this webhook is subscribed to are worth testing — testing an
    // event outside the webhook's scope would never be delivered.
    testableScopes() {
      const subscribed = this.webhook?.webhook?.scope || []
      return this.scope.filter((s) => subscribed.includes(s.id))
    },
  },
  created() {
    breadcrumbState.set([
      { label: 'Webhooks', to: '/developers/webhooks' },
      { label: this.webhookId },
    ])
    this.getWebhook(0, 25)
  },
  unmounted() {
    breadcrumbState.clear()
  },
  methods: {
    goBack() {
      this.$router.push('/developers/webhooks')
    },
    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATETIME_MED)
    },
    openEditScope() {
      this.editScopeRequest.scope = this.scope.filter((s) =>
        this.webhook.webhook.scope.includes(s.id)
      )
      this.showEditScopeModal = true
    },
    async updateScope() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.actionLoading = true

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/webhook/${this.webhookId}/scope`,
          {
            method: 'PATCH',
            body: JSON.stringify(this.editScopeRequest.scope.map((s) => s.id)),
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.showEditScopeModal = false
          this.getWebhook(0, 25)
        } else {
          toast.error('Could not update the webhook scopes.')
        }
      } catch (error) {
        toast.error('Could not update the webhook scopes.')
      } finally {
        this.actionLoading = false
      }
    },
    showData(data) {
      this.selectedEvent = data
    },

    async resend(event) {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.actionLoading = true

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/webhook/${this.webhookId}/event/${event.id}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.showTestWebhookForm = false
          this.getWebhook(0, 25)
          this.selectedEvent = undefined
        } else {
          toast.error(await this.actionErrorMessage(response))
        }
      } catch (error) {
        toast.error('Could not resend the event.')
      } finally {
        this.actionLoading = false
      }
    },

    async createTestWebhook() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.actionLoading = true

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/webhook/${this.webhookId}/test`,
          {
            method: 'POST',
            body: JSON.stringify(this.testEventRequest),
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.testEventRequest.eventType = ''
          this.showTestWebhookForm = false
          this.getWebhook(0, 25)
        } else {
          // Without this the dialog just sat there with no feedback, which reads as a hang.
          toast.error(await this.actionErrorMessage(response))
        }
      } catch (error) {
        toast.error('Could not send the test event.')
      } finally {
        this.actionLoading = false
      }
    },

    async actionErrorMessage(response) {
      try {
        const body = await response.text()
        if (body) return body
      } catch (e) {
        // Fall through to the status-based message.
      }
      return `The request failed (${response.status}).`
    },

    async getWebhook(page, size) {
      if (!this.webhookId) return

      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false

      try {
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/webhook/${this.webhookId}?size=${size}&page=${page}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()

          this.webhook = object
          this.serverItemsLength = this.webhook.webhookEvents.totalElements
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

    async deleteWebhook() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.actionLoading = true

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/webhook/${this.webhookId}`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.goBack()
        } else {
          toast.error('Could not delete the webhook.')
        }
      } catch (error) {
        toast.error('Could not delete the webhook.')
      } finally {
        this.actionLoading = false
      }
    },

    async deactivateWebhook() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.actionLoading = true

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/webhook/${this.webhookId}/deactivate`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.getWebhook(0, 25)
        } else {
          toast.error('Could not pause the webhook.')
        }
      } catch (error) {
        toast.error('Could not pause the webhook.')
      } finally {
        this.actionLoading = false
      }
    },

    async activateWebhook() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.actionLoading = true

        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/webhook/${this.webhookId}/activate`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          this.getWebhook(0, 25)
        } else {
          toast.error('Could not activate the webhook.')
        }
      } catch (error) {
        toast.error('Could not activate the webhook.')
      } finally {
        this.actionLoading = false
      }
    },
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getWebhook(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
